import React, { useState, useEffect } from "react";
import { Star, Instagram, Facebook, Music2 } from "lucide-react";
import secureStorage from "../../utils/secureStorage";
import { api } from "../../config/api";
import Notification from "../Common/Notification";
import { SettingsForm, FormSection, FormField, SaveAction, inputClass } from "./SettingsUI";

/**
 * Lien fiche Google Business (avis) + réseaux sociaux (Instagram, Facebook,
 * TikTok) configurés par le commerçant. Le POS les récupère au login
 * (champs bruts sur `Account`) pour les proposer au client en fin de vente.
 */
const SocialLinksSettings = () => {
    const [notification, setNotification] = useState({ show: false, type: "success", message: "" });
    const [accountInfo, setAccountInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [links, setLinks] = useState({
        googleReviewLink: "",
        instagramUrl: "",
        facebookUrl: "",
        tiktokUrl: "",
    });

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        setTimeout(() => {
            setNotification({ show: false, type: "", message: "" });
        }, 3000);
    };

    useEffect(() => {
        const loadAccountInfo = async () => {
            try {
                const account = await secureStorage.getItem("selectedAccount");
                if (account) {
                    setAccountInfo(account);
                    setLinks({
                        googleReviewLink: account.googleReviewLink || "",
                        instagramUrl: account.instagramUrl || "",
                        facebookUrl: account.facebookUrl || "",
                        tiktokUrl: account.tiktokUrl || "",
                    });
                }
            } catch (error) {
                console.error("Erreur lors du chargement des informations du compte:", error);
            }
        };

        loadAccountInfo();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setLinks((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        if (!accountInfo) return;
        try {
            setIsLoading(true);
            showNotification("loading", "Mise à jour des liens...");

            const response = await api.put(`/accounts/${accountInfo.id}`, links, {
                headers: {
                    "X-account-id": accountInfo.id,
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            const updatedAccount = { ...accountInfo, ...response.data };
            await secureStorage.setItem("selectedAccount", updatedAccount);
            setAccountInfo(updatedAccount);

            showNotification("success", "Liens mis à jour avec succès");
        } catch (error) {
            console.error("Error updating social links:", error);
            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur est survenue lors de la mise à jour";
            showNotification("error", errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const fields = [
        {
            key: "googleReviewLink",
            label: "Lien avis Google Business",
            placeholder: "https://g.page/r/votre-fiche/review",
            Icon: Star,
            help: "Envoyé au client en fin de vente pour lui demander de noter votre commerce sur Google.",
        },
        {
            key: "instagramUrl",
            label: "Instagram",
            placeholder: "https://instagram.com/votre_compte",
            Icon: Instagram,
        },
        {
            key: "facebookUrl",
            label: "Facebook",
            placeholder: "https://facebook.com/votre_page",
            Icon: Facebook,
        },
        {
            key: "tiktokUrl",
            label: "TikTok",
            placeholder: "https://tiktok.com/@votre_compte",
            Icon: Music2,
        },
    ];

    const renderField = ({ key, label, placeholder, Icon, help }) => (
        <FormField key={key} id={key} label={label} hint={help}>
            <div className="relative">
                <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                    id={key}
                    type="url"
                    name={key}
                    value={links[key]}
                    onChange={handleChange}
                    placeholder={placeholder}
                    className={`${inputClass(false)} pl-9`}
                />
            </div>
        </FormField>
    );

    return (
        <SettingsForm>
            {notification.show && (
                <Notification type={notification.type} message={notification.message} show={notification.show} />
            )}

            <SaveAction onClick={handleSave} saving={isLoading} disabled={!accountInfo} />

            <FormSection
                title="Avis Google"
                description="Ces liens peuvent être envoyés au client (WhatsApp) à la fin d'une vente, pour lui demander de laisser un avis ou de vous suivre."
            >
                {renderField(fields[0])}
            </FormSection>

            <FormSection title="Réseaux sociaux">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {fields.slice(1).map(renderField)}
                </div>
            </FormSection>
        </SettingsForm>
    );
};

export default SocialLinksSettings;
